import { Controller } from "@hotwired/stimulus"

// Tracker de dirty-state del editor visual de paginas.
//
// Con el modelo draft-en-DB, cada cambio del editor (create/update/
// destroy/reorder) persiste directo via su propio endpoint. Este
// controller solo se encarga de habilitar/deshabilitar los botones
// "Guardar" (publish snapshot) y "Descartar" (restore desde snapshot),
// y escuchar eventos de exito de forms Turbo dentro del editor.
//
// El boton "Guardar" dispara POST al endpoint de publish (Fase 4).
// El boton "Descartar" dispara DELETE al endpoint de discard (Fase 4).
//
// Targets:
//   saveBtn    — boton "Guardar"
//   saveLabel  — span con el texto "Guardar"
//   discardBtn — boton "Descartar"
//
// Values:
//   publishUrl     — POST endpoint (Fase 4)
//   discardUrl     — DELETE endpoint (Fase 4)
//   dirty          — flag inicial computado server-side
//   discardConfirm — mensaje de confirmacion al descartar
export default class extends Controller {
  static targets = ["saveBtn", "saveLabel", "discardBtn", "lastPublished"]
  static values = {
    publishUrl: String,
    discardUrl: String,
    dirty: { type: Boolean, default: false },
    discardConfirm: { type: String, default: "¿Descartar todos los cambios?" }
  }

  connect() {
    this.boundRecordSaved = this.#onRecordSaved.bind(this)
    this.boundSubmitEnd = this.#onSubmitEnd.bind(this)
    document.addEventListener("editor:record-saved", this.boundRecordSaved)
    document.addEventListener("turbo:submit-end", this.boundSubmitEnd)
    this.#refreshUI()
  }

  disconnect() {
    document.removeEventListener("editor:record-saved", this.boundRecordSaved)
    document.removeEventListener("turbo:submit-end", this.boundSubmitEnd)
  }

  // Cuando el header se re-renderiza via turbo_frame (ej: al cambiar state desde
  // el dropdown), los botones llegan con `disabled` HTML attr porque el partial
  // los renderiza asi por default. Sincronizamos la UI al estado actual del
  // store en cuanto los targets se re-conectan.
  saveBtnTargetConnected() {
    this.#refreshUI()
  }

  discardBtnTargetConnected() {
    this.#refreshUI()
  }

  async save(event) {
    event?.preventDefault?.()
    if (!this.dirtyValue || !this.publishUrlValue) return

    this.saveBtnTarget.disabled = true
    const response = await fetch(this.publishUrlValue, {
      method: "POST",
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": this.#csrfToken()
      }
    })
    const body = await response.text()
    if (response.ok) {
      this.#setDirty(false)
      this.#updateLastPublishedNow()
    }
    if (body && window.Turbo) window.Turbo.renderStreamMessage(body)
    this.#refreshUI()
  }

  async discard(event) {
    event?.preventDefault?.()
    if (!this.dirtyValue || !this.discardUrlValue) return
    if (!window.confirm(this.discardConfirmValue)) return

    this.discardBtnTarget.disabled = true
    const response = await fetch(this.discardUrlValue, {
      method: "DELETE",
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": this.#csrfToken()
      }
    })
    const body = await response.text()
    if (response.ok) this.#setDirty(false)
    if (body && window.Turbo) window.Turbo.renderStreamMessage(body)
    this.#refreshUI()
  }

  // --- Private ---

  #onRecordSaved() {
    this.#setDirty(true)
  }

  // Todo form submit dentro del editor que termina ok marca dirty.
  // Esto cubre inline-edits, forms de estilos, creates, destroys —
  // todo lo que hoy flywire a Turbo.
  #onSubmitEnd(event) {
    if (!event.detail?.success) return
    if (!this.element.contains(event.target)) return
    this.#setDirty(true)
  }

  #setDirty(value) {
    this.dirtyValue = value
    this.#refreshUI()
  }

  #refreshUI() {
    const disabled = !this.dirtyValue
    if (this.hasSaveBtnTarget) {
      this.saveBtnTarget.disabled = disabled
      this.saveBtnTarget.classList.toggle("opacity-50", disabled)
      this.saveBtnTarget.classList.toggle("cursor-not-allowed", disabled)
      this.saveBtnTarget.classList.toggle("pointer-events-none", disabled)
    }
    if (this.hasSaveLabelTarget) {
      const { cleanLabel, dirtyLabel } = this.saveBtnTarget?.dataset || {}
      if (cleanLabel && dirtyLabel) {
        this.saveLabelTarget.textContent = this.dirtyValue ? dirtyLabel : cleanLabel
      }
    }
    if (this.hasDiscardBtnTarget) {
      this.discardBtnTarget.disabled = disabled
      this.discardBtnTarget.classList.toggle("opacity-50", disabled)
      this.discardBtnTarget.classList.toggle("cursor-not-allowed", disabled)
      this.discardBtnTarget.classList.toggle("pointer-events-none", disabled)
    }
  }

  #updateLastPublishedNow() {
    if (!this.hasLastPublishedTarget) return
    const { justPublished } = this.lastPublishedTarget.dataset
    if (justPublished) this.lastPublishedTarget.textContent = justPublished
    this.lastPublishedTarget.setAttribute("title", new Date().toLocaleString())
  }

  #csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
